import React, { FunctionComponent, useCallback, useState } from 'react'
import { useTagSearch, useTimeoutEffect } from '../../../hooks/utils'
import {
  GridLayout,
  IconChip, IconsType,
  ItemSelect,
  MultiSelectSearch,
  RoundedButton,
  SearchInput,
  SelectSearch,
  Tag,
} from '../../../components/shared'
import {
  DocumentCategoryKeys,
} from '../../../model/DocumentCategory'

import { FiltersProps, FiltersType, SearchPanelFilters } from './Filters.model'

import styles from './Filters.module.scss'
import { VisibilityType } from '../../../constants'
import { SelectOption } from '../../../model/SelectOption'
import { VISIBILITY_FILTER_SELECT_VALUES } from '../../../misc/filters.utils'
import { ColorsV2 } from '../../../design-system/colors_v2'

const intialFilters = {
  search: '',
  documentCategories: [],
  tagIds: [],
  visibility: VISIBILITY_FILTER_SELECT_VALUES.find(
    (option) => option.value === VisibilityType.PRIVATE,
  ),
}

const Filters: FunctionComponent<FiltersProps> = ({
  tags,
  usedTags,
  capabilities,
  documentCategories,
  testId,
  onFiltersChange,
  onClearFilters,
  renderActionItem,
  hideActionItems = false,
  customVisibilityOptions,
  renderCustomFilters,
  resetPagination,
}) => {
  const [filters, setFilters] = useState<SearchPanelFilters>(intialFilters)

  const [tagOptions, tagValues] = useTagSearch(filters.tagIds, tags)

  useTimeoutEffect(
    () =>
      onFiltersChange({
        search: filters.search,
        documentCategories: filters.documentCategories,
        visibility: filters.visibility?.value,
        tagIds: filters.tagIds,
      }),
    500,
    [filters],
  )

  function onSearchChange(search) {
    if (search !== filters.search) {
      setFilters({
        ...filters,
        search,
      })
    }
  }

  const onDocumentCategoriesChange = (itemCategory: DocumentCategoryKeys) => {
    if (filters.documentCategories.includes(itemCategory)) {
      setFilters({
        ...filters,
        documentCategories: [],
      })
    } else {
      setFilters({ ...filters, documentCategories: [itemCategory] })
    }
  }

  const onTagsChange = (tagId: string) => {
    if (filters.tagIds.includes(tagId)) {
      setFilters({
        ...filters,
        tagIds: filters.tagIds.filter((currentTagId) => currentTagId !== tagId),
      })
    } else {
      setFilters({ ...filters, tagIds: [...filters.tagIds, tagId] })
    }
  }

  const handleVisibilityChange = useCallback(
    (visibility?: SelectOption<string>) => {
      setFilters({ ...filters, visibility })
    },
    [filters],
  )

  return (
    <div className={styles.container}>
      <GridLayout rowsTemplate="auto auto" gap="medium">
        <form onSubmit={(e) => e.preventDefault()}>
          <GridLayout rowsTemplate="auto" gap="medium" padding={['medium', 'none']}>
            {renderCustomFilters && renderCustomFilters(resetPagination)}
            {capabilities.includes(FiltersType.Title) && (
              <SearchInput
                value={filters.search}
                onChange={({ target: { value } }) => onSearchChange(value)}
                colorPreset="dark"
                placeholder="3 caractères min"
                autofocus
                testId={testId ? `input-${testId}-name-search` : undefined}
              />
            )}
            {capabilities.includes(FiltersType.Visibility) && (
              <SelectSearch
                value={filters.visibility}
                options={customVisibilityOptions ?? VISIBILITY_FILTER_SELECT_VALUES}
                colorPreset="dark"
                clearable={false}
                onSelect={handleVisibilityChange}
                testId="select_search-filters-by_visibility-search"
              />
            )}
            {capabilities.includes(FiltersType.Tags) && tags && (
              <>
                <MultiSelectSearch
                  placeholder="Rechercher par label"
                  icon="tag"
                  colorPreset="dark"
                  value={tagValues}
                  options={tagOptions}
                  onSelect={(selected) => {
                    const tagIds = selected.map(({ value }) => value)
                    setFilters({ ...filters, tagIds })
                  }}
                  testId="multi_select_search-filters-by_tag-search"
                />
                {usedTags && (
                  <ItemSelect
                    title="Labels utilisés récemment"
                    items={usedTags}
                    renderItem={(item) => (
                      <Tag
                        color={item.color}
                        selected={filters.tagIds.includes(item.id)}
                        title={item.label}
                        onClick={() => onTagsChange(item.id)}
                        testId={`filters-recently_used_tag_${item.id}`}
                      >
                        {item.label}
                      </Tag>
                    )}
                  />
                )}
              </>
            )}
            {capabilities.includes(FiltersType.DocumentCategory) && documentCategories && (
              <ItemSelect
                title="Type de document"
                items={documentCategories.map((category) => ({ ...category, id: category.id }))}
                renderItem={(item) => {
                  return (
                    <IconChip
                      colorPreset="light"
                      borderColor={item?.color as ColorsV2}
                      icon={item?.icon as IconsType}
                      label={item.label}
                      selected={filters.documentCategories.includes(item.id)}
                      onClick={() => onDocumentCategoriesChange(item.id)}
                      testId="icon_chip-document_type-select"
                    />
                  )
                }}
              />
            )}
          </GridLayout>
        </form>
      </GridLayout>
      {!hideActionItems && (
        <div className={styles.clearFilters}>
          {!renderActionItem && onClearFilters && (
            <RoundedButton
              theme="transparent"
              label="Effacer les filtres"
              onClick={() => {
                setFilters({
                  search: '',
                  documentCategories: [],
                  tagIds: [],
                })
                onClearFilters()
              }}
              testId="button-filters-erase"
            />
          )}
          {renderActionItem && (
            <div
              onClick={() => {
                setFilters({
                  search: '',
                  documentCategories: [],
                  tagIds: [],
                })
              }}
            >
              {renderActionItem()}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Filters
